import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Drawer,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  Typography,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { getSidebarConfigWithFlags } from "./SidebarConfigWithFlags";
import { SidebarMenu } from "./SidebarMenu";
import { SealIcon } from "../../../seal-icon";
import { useUserContext } from "../../../../hooks/userContext";
import { useDesignerApplicationContext } from "../../../../hooks/designer-application-context";
import "./styles.scss";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { PlanKey } from "../../../../pages/registration/homeowner-plan-selection-fieldset";
import { DesignerApplicationSteps } from "../../../../__generated__/types";
import { useEffectiveRole } from "../../../../hooks/useEffectiveRole";
import { useFeatureFlags } from "../../../../contexts/FeatureFlagsContext";
import { useDesignerOnboardingAccess } from "../../../../hooks/useDesignerOnboardingAccess";
import { useHomeownerOnboardingAccess } from "../../../../hooks/useHomeownerOnboardingAccess";
import { fetchMatches } from "../../../../services/matchesApi";

interface Props {
  showScrollbar: boolean;
  handleSidebarScroll: () => void
}

export const DashboardSidebar = ({ showScrollbar, handleSidebarScroll }: Props) => {
  const { user } = useUserContext();
  const {
    canAccessAdminFeatures,
    canAccessFeature,
    effectiveRole,
    effectiveTier,
    isDesigner,
  } = useEffectiveRole();
  const { application } = useDesignerApplicationContext();
  const featureFlags = useFeatureFlags();
  const onboardingAccess = useDesignerOnboardingAccess();
  const homeownerOnboardingAccess = useHomeownerOnboardingAccess();
  const [matchesBadgeCount, setMatchesBadgeCount] = useState<number>(0);
  const viewerAccountType =
    effectiveRole === "Homeowner" || effectiveRole === "Designer"
      ? effectiveRole
      : user?.accountType ?? null;
  const isAdminHomeownerPreview =
    canAccessAdminFeatures &&
    effectiveRole === "Homeowner" &&
    (user?.accountType ?? "").toLowerCase() !== "homeowner";

  // Add global styles to override any conflicting CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .sidebar-content .MuiTypography-root {
        font-size: 13px !important;
        font-weight: 500 !important;
      }
      .sidebar-content .MuiSvgIcon-root:not([data-logo="true"]) {
        font-size: 16px !important;
        width: 16px !important;
        height: 16px !important;
      }
      .sidebar-content svg:not([data-logo="true"]) {
        font-size: 16px !important;
        width: 16px !important;
        height: 16px !important;
      }
      /* Explicitly force logo to be 32px */
      .sidebar-content svg[data-logo="true"] {
        font-size: 32px !important;
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
      }
      /* Force all SVG elements with data-logo to be 32px */
      svg[data-logo="true"] {
        font-size: 32px !important;
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // Consider designer "active" for nav unlocks only when membership is active (after approval/payment)
  const isDesignerMembershipActive = isDesigner && (
    application?.status === DesignerApplicationSteps.MembershipActive
  );
  
  // Check if Matches is locked for designers (same logic as sidebar config)
  const isMatchesLocked = isDesigner && !(onboardingAccess?.isMatchingProfileUnlocked ?? isDesignerMembershipActive);
  
  // Get user's subscription plan and map GraphQL enum values to PlanKey
  // Handle both object format { value: "GuildPlus" } and string format "GuildPlus"
  const rawSubscription = typeof user?.subscription === 'string' 
    ? user.subscription 
    : (user?.subscription as any)?.homeownerValue ||
      (user?.subscription as any)?.designerValue ||
      (user?.subscription as any)?.value ||
      (user?.subscription as any)?.tier ||
      'basic';
  
  // Map GraphQL enum values to PlanKey type
  const mapSubscriptionToPlanKey = (sub: string): PlanKey => {
    const normalized = String(sub ?? "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

    if (!normalized || normalized === "free" || normalized === "basic") {
      return "basic";
    }
    if (
      normalized === "guildannually" ||
      normalized === "guildplus" ||
      normalized === "guildplusbeta" ||
      normalized === "guild+"
    ) {
      return "GuildPlus";
    }
    if (
      normalized === "guildmonthly" ||
      normalized === "guild" ||
      normalized === "stylefilemonthly" ||
      normalized === "stylefileannually"
    ) {
      return "Guild";
    }
    return 'basic';
  };
  
  // Use effective tier from impersonation context if available, otherwise use actual subscription
  const effectivePlan: PlanKey = effectiveTier 
    ? (effectiveTier === 'Guild+' ? 'GuildPlus' : effectiveTier) as PlanKey
    : mapSubscriptionToPlanKey(rawSubscription);
  const effectiveHomeownerOnboardingAccess = !isDesigner
    ? isAdminHomeownerPreview
      ? {
          matchingProfileUnlocked: true,
          bioUnlocked: true,
        }
      : {
          matchingProfileUnlocked: homeownerOnboardingAccess.matchingProfileUnlocked,
          bioUnlocked: homeownerOnboardingAccess.bioUnlocked,
        }
    : undefined;
  

  // Use new config with feature flags
  const config = getSidebarConfigWithFlags({
    isDesigner,
    userPlan: effectivePlan,
    isDesignerApproved: isDesignerMembershipActive,
    featureFlags,
    onboardingAccess: isDesigner ? onboardingAccess : undefined,
    homeownerOnboardingAccess: effectiveHomeownerOnboardingAccess,
    canAccessFeature,
    matchesBadgeCount
  });

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(true);

  const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);

  const scrollableRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollableRef.current;
    if (el)  el.addEventListener("wheel", handleSidebarScroll, { passive: true });

    return () => {
      if (el) el.removeEventListener("wheel", handleSidebarScroll);
    };
  }, [handleSidebarScroll]);

  useEffect(() => {
    if (!user?.id) return;
    const previewRole = isAdminHomeownerPreview ? "homeowner" : undefined;
    const accountType = (viewerAccountType ?? "").trim();
    if (accountType !== "Designer" && accountType !== "Homeowner") return;
    let cancelled = false;

    fetchMatches(user.id, 1, previewRole ? { previewRole } : undefined)
      .then((data) => {
        if (cancelled) return;
        const total = data.total ?? data.matches?.length ?? 0;
        setMatchesBadgeCount(total);
      })
      .catch(() => {
        if (!cancelled) {
          setMatchesBadgeCount(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAdminHomeownerPreview, user?.id, viewerAccountType]);

  return (
    <>
      {isMobile && (
        <>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={toggleDrawer(true)}
          sx={{
            color: "#fff",
            top: 16,
            left: 16,
            zIndex: 1300,
          }}
        >
          <MenuIcon sx={{ fontSize: '16px' }} />
        </IconButton>
        <Box sx={{ position: "absolute", alignSelf: "center", left: 60 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton
              onClick={() => !isMatchesLocked && navigate("/matches")}
              disabled={isMatchesLocked}
              sx={{ 
                color: isMatchesLocked ? "rgba(255, 255, 255, 0.5)" : "#fff",
                opacity: isMatchesLocked ? 0.6 : 1,
                cursor: isMatchesLocked ? 'not-allowed' : 'pointer'
              }}
            >
              {isMatchesLocked ? (
                <LockOutlinedIcon sx={{ fontSize: '16px' }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: '16px' }} />
              )}
            </IconButton>
            <Typography 
              variant="body1" 
              sx={{ 
                color: isMatchesLocked ? "rgba(255, 255, 255, 0.5)" : "#fff", 
                marginLeft: "0 !important",
                opacity: isMatchesLocked ? 0.6 : 1,
                cursor: isMatchesLocked ? 'not-allowed' : 'default'
              }}
            >
              Matches
            </Typography>
          </Stack>
        </Box>
        </>
      )}

      <Drawer
        anchor="left"
        open={drawerOpen || !isMobile}
        onClose={toggleDrawer(false)}
        variant={isMobile ? "temporary" : "permanent"}
        PaperProps={{
          sx: {
            width: "220px !important", // Reduced from 260px for more modern feel - UPDATED
            backgroundColor: "#374a67", // Consistent dark blue
            color: "#fff",
            pt: 1.5, // Reduced padding
            borderRight: 0,
            borderRadius: isMobile ? "0 20px 20px 0" : "0",
            boxShadow: "none", // Cleaner look without shadow
            "& .MuiPaper-root": {
              width: "220px !important",
            },
          },
        }}
      >
        <Stack 
          spacing={1.5} // Reduced spacing between sections
          className={`sidebar-content ${!isMobile && showScrollbar ? "scroll-visible" : ""}`}
          ref={scrollableRef}
          onMouseEnter={handleSidebarScroll}
          onWheel={handleSidebarScroll}
          sx={{ px: 1 }} // Add subtle horizontal padding
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            px={0.5}
          >
            {isMobile && (
              <IconButton onClick={toggleDrawer(false)} sx={{ color: "#fff" }}>
                <CloseIcon />
              </IconButton>
            )}  
            <IconButton
              component="a"
              href="https://homedesignguild.com/"
              aria-label="Go to Home Design Guild main website"
              onClick={(event) => event.stopPropagation()}
              sx={{
                p: 0,
                borderRadius: 0,
                width: "32px !important",
                height: "32px !important",
                "& svg": {
                  width: "32px !important",
                  height: "32px !important",
                  fontSize: "32px !important",
                },
              }}
            >
              <SealIcon
                color="#fff"
                width={32}
                height={32}
                data-logo="true"
              />
            </IconButton>
          </Stack>
          <SidebarMenu config={config} />
        </Stack>
      </Drawer>

    </>
  );
};
